import type { Route } from '@metro/routing'
import type { SchemaConnection, SchemaData, SchemaTransition } from '../schema/types'

/**
 * Extract the set of element ids (connection / transition / station) that
 * make up the selected `Route`. The renderer uses this set to re-draw the
 * matching pre-tessellated MMTG geometry on top of the dim pass at full
 * opacity, so the highlighted route looks IDENTICAL in shape and colour
 * to the underlying map.
 *
 * Pixel-parity port of iOS
 * `metro-ios/MetroCore/Sources/Routing/RouteElementIds.swift`.
 *
 * Resolution cascade for `transfer` / `walk` legs (mirrors iOS):
 *   1. Explicit `transition_id` → `transitionById[id]`.
 *   2. Pair fallback → `transitionByPair["<from>→<to>"]`.
 *   3. BFS chain through the real-transition graph (depth cap 6) —
 *      decomposes a synthetic / unresolvable transfer into the shortest
 *      chain of MMTG-renderable transitions through the hub's per-line
 *      platforms (Paddington / Bank-Monument cascade).
 *   4. **Transfer legs only** — same-line connection fallback. Walk legs
 *      render via `WalkSegmentOverlay`; lighting up a tube polyline
 *      underneath would falsely say "ride the line" (canonical failure:
 *      OXC → Tottenham Court Road walk variant turning on the Central
 *      line between the two stations).
 *
 * Stations / connections / transitions referenced by the route but missing
 * from the schema are silently skipped (with a one-shot dev warn) — schema
 * drift mid-render must not crash the overlay.
 *
 * Bad-wire hygiene (cross-platform contract — mirrors iOS
 * `RouteElementIds.swift` @1ec46bab5): for each transfer/walk leg, BEFORE
 * the resolution cascade, the endpoints are sanitised against the
 * adjacent ride legs (defence-in-depth — the backend TFL mapper is the
 * primary fix, see backend task #6):
 *   - **effectiveTo**: an empty `to_station_id` is inferred from the NEXT
 *     ride leg's first non-empty stop (a tube↔Elizabeth interchange often
 *     arrives with a null `to` on the wire).
 *   - **effectiveFrom**: an empty `from_station_id` is inferred from the
 *     PRIOR ride leg's last non-empty stop; a NON-empty `from` that names
 *     the same physical hub on a DIFFERENT platform than the prior ride's
 *     last stop is rewritten to that prior stop (so the highlight follows
 *     the platform the rider actually arrived on, not a sibling line's
 *     platform). "Same hub" ⇔ equal schema `naptanId` of the two stops; a
 *     different NaPTAN is a genuinely different station and is left
 *     verbatim (never rewritten).
 *   - The cascade then resolves against (effectiveFrom, effectiveTo) and
 *     both are inserted as station highlights.
 * A ride stop with an empty `stationId` is treated as absent (pushes
 * nothing, never enters the set) and its two real neighbours resolve their
 * direct connection across the gap.
 */
export function extractRouteElementIds(route: Route, schema: SchemaData): ReadonlySet<string> {
  const ids = new Set<string>()
  if (route.legs.length === 0) return ids

  // O(1) lookup keyed by (lineId, fromId, toId). Reversible connections
  // duplicated under the reversed pair so a leg traversed backwards
  // still resolves on the first lookup.
  const connectionByEdge = new Map<string, SchemaConnection>()
  const connectionKey = (lineId: string, fromId: string, toId: string): string =>
    `${lineId}|${fromId}|${toId}`
  // Directed per-line adjacency for `_bfsConnectionPath` — keyed by
  // `${lineId}|${stationId}`, value is the list of onward edges (the
  // connection + the station reached). The reverse edge is added ONLY when
  // the connection is `reversible` (fix c, mirrors iOS RouteElementIds): a
  // one-way junction connector (e.g. a Northern branch split at Camden
  // Town) must not be walked backwards, or the shortest-hop BFS picks the
  // wrong branch and the highlight zig-zags. Used to recover a continuous
  // edge chain when a backend leg's consecutive stops SKIP an intermediate
  // schema stop (no direct edge exists across the gap).
  const connectionAdjacency = new Map<string, { conn: SchemaConnection; next: string }[]>()
  const pushConnAdjacency = (key: string, conn: SchemaConnection, next: string): void => {
    let list = connectionAdjacency.get(key)
    if (list === undefined) {
      list = []
      connectionAdjacency.set(key, list)
    }
    list.push({ conn, next })
  }
  const connAdjacencyKey = (lineId: string, stationId: string): string => `${lineId}|${stationId}`
  for (const c of schema.connections) {
    if (c.fromStationId === undefined || c.toStationId === undefined) continue
    connectionByEdge.set(connectionKey(c.lineId, c.fromStationId, c.toStationId), c)
    if (c.reversible) {
      connectionByEdge.set(connectionKey(c.lineId, c.toStationId, c.fromStationId), c)
    }
    // Forward edge always; reverse adjacency ONLY when reversible (see
    // comment above) — directed so a one-way branch connector can't be
    // traversed backwards into the wrong branch.
    pushConnAdjacency(connAdjacencyKey(c.lineId, c.fromStationId), c, c.toStationId)
    if (c.reversible) {
      pushConnAdjacency(connAdjacencyKey(c.lineId, c.toStationId), c, c.fromStationId)
    }
  }

  // Line and physical station of a stop come from the schema, never from
  // the id's spelling: v1 ids read `station-{line}-{code}`, v2 ids
  // `{line}.{naptan}` (TASK-986).
  const stationById = new Map(schema.stations.map((st) => [st.id, st] as const))
  // The line both stops sit on, or null when either is unknown or they differ.
  const sharedLine = (a: string, b: string): string | null => {
    const la = stationById.get(a)?.line.id
    const lb = stationById.get(b)?.line.id
    return la !== undefined && la === lb ? la : null
  }
  // `true` when `a` and `b` are different stops of the SAME physical
  // station (equal NaPTAN) — a different line's platform of one
  // interchange. A different NaPTAN is a genuinely different station and
  // returns `false` (the E hygiene rewrite must NOT touch it).
  const isSameHubDifferentPlatform = (a: string, b: string): boolean => {
    if (a === b) return false
    const na = stationById.get(a)?.naptanId
    return na !== undefined && na.length > 0 && na === stationById.get(b)?.naptanId
  }

  const transitions: readonly SchemaTransition[] = schema.transitions ?? []
  const transitionById = new Map<string, SchemaTransition>()
  const transitionByPair = new Map<string, SchemaTransition>()
  // Adjacency for `_bfsTransitionChain` — keyed by stationId, value is
  // the list of real transitions touching that node. Used to decompose a
  // synthetic "overlay" transfer (e.g. Paddington `h&c-PAC ↔ bak-PAC`,
  // which has no baked MMTG pill) into the shortest chain of real,
  // MMTG-renderable transitions so the highlight pass pops every pill
  // along the indoor walk.
  const transitionAdjacency = new Map<string, SchemaTransition[]>()
  const pushAdjacency = (key: string, t: SchemaTransition): void => {
    let list = transitionAdjacency.get(key)
    if (list === undefined) {
      list = []
      transitionAdjacency.set(key, list)
    }
    list.push(t)
  }
  const transitionPairKey = (fromId: string, toId: string): string => `${fromId}→${toId}`
  for (const t of transitions) {
    transitionById.set(t.id, t)
    transitionByPair.set(transitionPairKey(t.fromStationId, t.toStationId), t)
    pushAdjacency(t.fromStationId, t)
    if (t.reversible) {
      transitionByPair.set(transitionPairKey(t.toStationId, t.fromStationId), t)
      pushAdjacency(t.toStationId, t)
    }
  }

  let missingConnections = 0
  let missingTransitions = 0

  // Bad-wire hygiene (cross-platform contract — mirrors iOS
  // RouteElementIds.swift @4d67ef07b): the last non-empty ride stop seen
  // so far, used to recover a transfer/walk leg whose `from_station_id`
  // arrived empty on the wire by inferring it from where the prior ride
  // left off. Forward hardening only — does NOT fix the ABW→CTN render
  // gap, where the interchange stop is ABSENT on the wire (not empty);
  // that defect is owned by the backend schema mapper.
  let lastRideStationID = ''

  for (let legIndex = 0; legIndex < route.legs.length; legIndex++) {
    const leg = route.legs[legIndex]
    if (leg.type === 'ride') {
      const stops = leg.stops ?? []
      const lineId = leg.line_id ?? ''
      // Walk the stops with a `prevNonEmpty` cursor so an empty-stationId
      // stop is treated as ABSENT: it pushes nothing, never advances the
      // cursor, and never breaks the chain — its two real neighbours
      // resolve their DIRECT connection across the gap. An empty id never
      // enters the result set.
      let prevNonEmpty = ''
      for (const stop of stops) {
        if (stop.stationId === '') continue
        // Skip the station highlight for passthrough stops — the train
        // passes through without stopping, so the map should keep the
        // dim layer on the station marker / label while still drawing
        // the polyline edge between its neighbours. Endpoints of a
        // ride are always real stops so this never silences A/B.
        if (stop.passthrough !== true) ids.add(stop.stationId)
        lastRideStationID = stop.stationId
        if (prevNonEmpty === '') {
          prevNonEmpty = stop.stationId
          continue
        }
        const connection = connectionByEdge.get(connectionKey(lineId, prevNonEmpty, stop.stationId))
        if (connection !== undefined) {
          ids.add(connection.id)
        } else {
          // No DIRECT edge between the two consecutive stops — the backend
          // leg skipped an intermediate schema stop (interchange). Resolve
          // the SHORTEST same-line path over the connection graph and light
          // up every edge + every intermediate station so the highlight
          // stays continuous. Falls back to the old `missingConnections++`
          // when no path exists within the hop cap.
          const path = _bfsConnectionPath(
            lineId,
            prevNonEmpty,
            stop.stationId,
            connectionAdjacency,
            connAdjacencyKey,
          )
          if (path !== null) {
            for (const step of path) {
              ids.add(step.conn.id)
              // `step.node` is the station reached after this edge; the
              // final one equals `stop.stationId` (already added) — the
              // Set makes the re-add idempotent, and the intermediate
              // nodes become continuous highlights.
              ids.add(step.node)
            }
          } else {
            missingConnections++
          }
        }
        prevNonEmpty = stop.stationId
      }
      continue
    }
    // transfer / walk leg — four-level resolution cascade, run against
    // the SANITISED endpoints (effectiveFrom, effectiveTo). See the
    // bad-wire hygiene block in the file doc-comment (mirrors iOS
    // @1ec46bab5; backend task #6 is the primary fix).
    //
    // effectiveTo: an empty `to_station_id` is inferred from the NEXT ride
    // leg's first non-empty stop (the tube↔Elizabeth interchange arrives
    // with a null `to` on the wire). A trailing transfer with no following
    // ride leaves it empty → only the non-empty `from` is pushed.
    const rawToId = leg.to_station_id ?? ''
    const toId = rawToId === '' ? _nextRideFirstStop(route.legs, legIndex) : rawToId
    // effectiveFrom: empty → prior ride's last non-empty stop. A NON-empty
    // `from` that names the SAME hub on a DIFFERENT platform than the prior
    // ride's last stop is rewritten to that prior stop (follow the platform
    // the rider arrived on, not a sibling line's). A different NaPTAN is a
    // genuinely different station → left verbatim. A leading transfer
    // with empty from and no prior ride leaves effectiveFrom empty.
    const fromId = leg.from_station_id ?? ''
    let effectiveFrom: string
    if (fromId === '') {
      effectiveFrom = lastRideStationID
    } else if (
      lastRideStationID !== '' &&
      fromId !== lastRideStationID &&
      isSameHubDifferentPlatform(fromId, lastRideStationID)
    ) {
      effectiveFrom = lastRideStationID
    } else {
      effectiveFrom = fromId
    }
    let resolved: SchemaTransition | undefined
    const explicitId = leg.transition_id ?? ''
    if (explicitId.length > 0) {
      resolved = transitionById.get(explicitId)
    }
    if (
      resolved === undefined &&
      effectiveFrom.length > 0 &&
      toId.length > 0 &&
      effectiveFrom !== toId
    ) {
      resolved = transitionByPair.get(transitionPairKey(effectiveFrom, toId))
    }
    if (resolved !== undefined) {
      ids.add(resolved.id)
    } else if (effectiveFrom.length > 0 && toId.length > 0 && effectiveFrom !== toId) {
      const chain = _bfsTransitionChain(effectiveFrom, toId, transitionAdjacency)
      if (chain !== null && chain.length > 0) {
        for (const t of chain) ids.add(t.id)
      } else if (leg.type === 'transfer') {
        // Same-line connection fallback — transfer legs only. For walks
        // the WalkSegmentOverlay draws the pictogram; highlighting a
        // tube polyline underneath would falsely signal "ride the
        // line". Canonical failure: OXC → TCR walk variant lighting up
        // the Central line.
        const line = sharedLine(effectiveFrom, toId)
        if (line !== null) {
          const c = connectionByEdge.get(connectionKey(line, effectiveFrom, toId))
          if (c !== undefined) {
            ids.add(c.id)
          } else if (explicitId.length > 0) {
            missingTransitions++
          }
        } else if (explicitId.length > 0) {
          missingTransitions++
        }
      } else if (explicitId.length > 0) {
        missingTransitions++
      }
    } else if (explicitId.length > 0) {
      missingTransitions++
    }
    if (effectiveFrom.length > 0) ids.add(effectiveFrom)
    if (toId.length > 0) ids.add(toId)
  }

  if ((missingConnections > 0 || missingTransitions > 0) && process.env.NODE_ENV !== 'production') {
    console.warn(
      `[extractRouteElementIds] schema drift: ${missingConnections} connection(s), ` +
        `${missingTransitions} transition(s) referenced by the route but absent from schema.`,
    )
  }

  return ids
}

/**
 * First non-empty ride stop AFTER `legIndex` — the station the rider
 * boards next. Used to recover a transfer/walk leg whose `to_station_id`
 * arrived empty on the wire (E hygiene). Returns '' when no following ride
 * leg has a non-empty stop.
 */
function _nextRideFirstStop(legs: Route['legs'], legIndex: number): string {
  for (let i = legIndex + 1; i < legs.length; i++) {
    const leg = legs[i]
    if (leg.type !== 'ride') continue
    for (const stop of leg.stops ?? []) {
      if (stop.stationId !== '') return stop.stationId
    }
  }
  return ''
}

/**
 * BFS over the same-line CONNECTION graph to find the shortest chain of
 * edges connecting `from` and `to` along `lineId`. Returns the ordered
 * list of `{ conn, node }` steps (where `node` is the station reached
 * after traversing `conn`) or null when no path exists within the hop
 * cap. Used by the ride-leg highlight to bridge a gap where the backend
 * leg's consecutive stops skip an intermediate schema stop — every edge
 * and every intermediate station on the resolved path is highlighted so
 * the route line stays continuous.
 *
 * Connections are bidirectional in `adjacency` (both endpoints indexed),
 * so a leg traversed in either direction resolves.
 *
 * Bridge bound (`MAX_BRIDGE_HOPS`, cross-platform contract — mirror on iOS
 * `RouteElementResolver.sameLineConnectionPath` + Android
 * `RouteHighlightExtractor.bfsSameLineConnectionPath`): a same-line
 * CONNECTION graph is not always a simple chain. The Northern line is a
 * TREE whose two branches (via Bank / via Charing Cross) only rejoin far
 * apart at Oval/Kennington, and whose top-junction platform records at
 * Camden Town (`northern.940GZZLUCTN-0` Bank-side vs. `northern.940GZZLUCTN-2`
 * Charing-Cross-side) carry NO direct same-line edge. A ride leg that
 * crosses branches there misses the direct edge and, WITHOUT a bound,
 * BFS returns the ONLY tree path — a ~20-hop wrap down the entire other
 * branch, across the rejoin and back up: a phantom closed loop of
 * off-route connections + station dots on the map overlay.
 *
 * A legit interchange-skip bridge (the reason this fallback exists) spans
 * at most a handful of stops — every observed real case skips a single
 * interchange record (1 intermediate = 2 hops). The branch-ring wrap is an
 * order of magnitude longer (19-22 hops on the real Northern schema). So
 * the search is capped at `MAX_BRIDGE_HOPS`: a genuine short skip resolves;
 * a wrap exceeds the cap, BFS returns null, and the caller draws NOTHING
 * extra (`missingConnections++`) instead of the phantom ring. This is a
 * topological guard, not a per-line constant — it bounds the DETOUR length,
 * which is tiny for real skips and huge for a branch wrap.
 */
function _bfsConnectionPath(
  lineId: string,
  from: string,
  to: string,
  adjacency: Map<string, { conn: SchemaConnection; next: string }[]>,
  keyFor: (lineId: string, stationId: string) => string,
): { conn: SchemaConnection; node: string }[] | null {
  if (from === to) return []
  const queue: { node: string; path: { conn: SchemaConnection; node: string }[] }[] = [
    { node: from, path: [] },
  ]
  const seen = new Set<string>([from])
  // Bound the bridge to a short interchange-skip. See the function
  // doc-comment: a wrap around a branch ring (Northern via Bank / via
  // Charing Cross) is ~20 hops and must NOT be emitted; a real skip is 1-3.
  const MAX_BRIDGE_HOPS = 4
  while (queue.length > 0) {
    const head = queue.shift()
    if (head === undefined) break
    const { node, path } = head
    if (path.length >= MAX_BRIDGE_HOPS) continue
    const neighbours = adjacency.get(keyFor(lineId, node))
    if (neighbours === undefined) continue
    for (const edge of neighbours) {
      const next = edge.next
      if (seen.has(next)) continue
      const newPath = path.concat({ conn: edge.conn, node: next })
      if (next === to) return newPath
      seen.add(next)
      queue.push({ node: next, path: newPath })
    }
  }
  return null
}

/**
 * BFS over the real-transition graph to find the shortest chain
 * connecting `from` and `to` through intermediate per-line platforms at
 * the same physical hub. Returns null when no chain exists. Depth cap 6
 * matches real-world hub depth (Paddington / Bank-Monument) — safety
 * belt against pathological cycles, not a product-driven constraint.
 */
function _bfsTransitionChain(
  from: string,
  to: string,
  adjacency: Map<string, SchemaTransition[]>,
): SchemaTransition[] | null {
  if (from === to) return []
  const queue: { node: string; path: SchemaTransition[] }[] = [{ node: from, path: [] }]
  const seen = new Set<string>([from])
  const maxDepth = 6
  while (queue.length > 0) {
    const head = queue.shift()
    if (head === undefined) break
    const { node, path } = head
    if (path.length >= maxDepth) continue
    const neighbours = adjacency.get(node)
    if (neighbours === undefined) continue
    for (const t of neighbours) {
      const next = t.fromStationId === node ? t.toStationId : t.fromStationId
      if (seen.has(next)) continue
      const newPath = path.concat(t)
      if (next === to) return newPath
      seen.add(next)
      queue.push({ node: next, path: newPath })
    }
  }
  return null
}
